import { useSession } from "../lib/auth-client";
import { useTicketStats, useTicketDailyCounts } from "@/hooks/tickets";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

function formatDuration(ms: number | null): string {
  if (ms === null) return "—";
  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
}

interface StatCardProps {
  title: string;
  description: string;
  value: string | number;
  loading: boolean;
}

function StatCard({ title, description, value, loading }: StatCardProps) {
  return (
    <Card className="card-glow">
      <CardHeader>
        <CardDescription className="text-[10px] font-bold tracking-[0.12em] uppercase" style={{ color: 'var(--muted-foreground)' }}>{title}</CardDescription>
        <CardTitle className="text-3xl font-bold tabular-nums font-syne">
          {loading ? <Skeleton className="h-9 w-24" /> : value}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{description}</p>
      </CardContent>
    </Card>
  );
}

function buildLast30Days(apiData: { day: string; count: number }[]) {
  const map = new Map(apiData.map((d) => [d.day, d.count]));
  const days = [];
  for (let i = 29; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    days.push({ day: key, count: map.get(key) ?? 0 });
  }
  return days;
}

function formatChartDate(day: string) {
  const d = new Date(day + "T00:00:00");
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function HomePage() {
  const { data: session } = useSession();
  const { data: stats, isLoading } = useTicketStats();
  const { data: dailyData, isLoading: dailyLoading } = useTicketDailyCounts();

  const chartData = dailyData ? buildLast30Days(dailyData.data) : [];

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto">
      <div className="mb-8">
        <p className="text-[11px] font-bold tracking-[0.15em] uppercase mb-1" style={{ color: 'oklch(0.65 0.22 285)' }}>
          Dashboard
        </p>
        <h1 className="text-2xl font-bold font-syne">Welcome back, {session?.user?.name}</h1>
        <p className="text-sm mt-1 text-muted-foreground">Here's an overview of your helpdesk.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard
          title="Total Tickets"
          description="All tickets ever submitted"
          value={stats?.totalTickets ?? 0}
          loading={isLoading}
        />
        <StatCard
          title="Open Tickets"
          description="New, processing, or waiting for an agent"
          value={stats?.openTickets ?? 0}
          loading={isLoading}
        />
        <StatCard
          title="Resolved by AI"
          description="Tickets closed automatically without agent intervention"
          value={stats?.resolvedByAI ?? 0}
          loading={isLoading}
        />
        <StatCard
          title="AI Resolution Rate"
          description="Percentage of all tickets resolved by AI"
          value={isLoading ? 0 : `${stats?.percentResolvedByAI ?? 0}%`}
          loading={isLoading}
        />
        <StatCard
          title="Avg. Resolution Time"
          description="Average time from ticket creation to resolution"
          value={formatDuration(stats?.avgResolutionTimeMs ?? null)}
          loading={isLoading}
        />
      </div>

      <Card className="mt-6 card-glow">
        <CardHeader>
          <CardTitle className="text-[10px] font-bold tracking-[0.12em] uppercase font-syne" style={{ color: 'var(--muted-foreground)' }}>
            Tickets per Day
          </CardTitle>
          <CardDescription style={{ color: 'var(--muted-foreground)' }}>
            Total tickets submitted over the past 30 days
          </CardDescription>
        </CardHeader>
        <CardContent>
          {dailyLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid vertical={false} strokeDasharray="2 4" stroke="oklch(1 0 0 / 0.06)" />
                <XAxis
                  dataKey="day"
                  tickFormatter={formatChartDate}
                  tick={{ fontSize: 10, fill: 'var(--muted-foreground)', fontFamily: 'IBM Plex Mono' }}
                  tickLine={false}
                  axisLine={false}
                  interval={4}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 10, fill: 'var(--muted-foreground)', fontFamily: 'IBM Plex Mono' }}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  formatter={(value) => [value, "Tickets"]}
                  labelFormatter={(label) => typeof label === 'string' ? formatChartDate(label) : label}
                  contentStyle={{
                    background: 'var(--popover)',
                    border: '1px solid oklch(0.65 0.22 285 / 0.3)',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontFamily: 'IBM Plex Mono',
                    color: 'var(--popover-foreground)',
                  }}
                  cursor={{ fill: 'oklch(0.65 0.22 285 / 0.08)' }}
                />
                <Bar dataKey="count" radius={[3, 3, 0, 0]} fill="oklch(0.65 0.22 285)" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
